import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BreadcrumbGroup,
  Box,
  Button,
  Cards,
  ColumnLayout,
  Container,
  FormField,
  Header,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  Table,
  Tabs,
  Alert,
  Badge,
  Modal,
  Select,
  Pagination,
  Textarea,
  Icon,
  TextFilter,
} from '@cloudscape-design/components';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../AuthContext';
import { useNotifications } from './NotificationContext';
import AppLayoutWrapper from './AppLayoutWrapper';
import './Billing.css';

// Map pack names to their Adexcloud purchase page URLs
const ADEXCLOUD_PACK_URLS = {
  'startup & tpe': 'https://predash.adexcloud.dz/dashboard/ai-services/Chatbot%20Startup%20&%20TPE/new-instance',
  'pme': 'https://predash.adexcloud.dz/dashboard/ai-services/Chatbot%20PME/new-instance',
  'pme advanced': 'https://predash.adexcloud.dz/dashboard/ai-services/Chatbot%20PME%20Advanced/new-instance',
  'corporate': 'https://predash.adexcloud.dz/dashboard/ai-services/Chatbot%20Corporate/new-instance',
  'corporate plus': 'https://predash.adexcloud.dz/dashboard/ai-services/Chatbot%20Corporate%20Plus/new-instance',
};

const getAdexcloudPurchaseUrl = (pack) => {
  const key = (pack?.name || '').trim().toLowerCase();
  return ADEXCLOUD_PACK_URLS[key] || null;
};

export default function Billing() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { success: notifySuccess, error: notifyError } = useNotifications();

  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [availablePacks, setAvailablePacks] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [quotaStatus, setQuotaStatus] = useState({ packs: [], resources: {} });
  const [activeTab, setActiveTab] = useState('store');
  const [highlightedPackId, setHighlightedPackId] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedPackForAssign, setSelectedPackForAssign] = useState(null);
  const [selectedAgentForAssign, setSelectedAgentForAssign] = useState(null);
  const [assignableAgents, setAssignableAgents] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [purchaseSortingColumn, setPurchaseSortingColumn] = useState(null);
  const [purchaseSortingDescending, setPurchaseSortingDescending] = useState(true);

  const [consumptionHistory, setConsumptionHistory] = useState([]);
  const [consumptionGraphs, setConsumptionGraphs] = useState([]);
  const [callDetails, setCallDetails] = useState([]);
  const [callDetailsTotal, setCallDetailsTotal] = useState(0);
  const [callDetailsPage, setCallDetailsPage] = useState(1);
  const [callDetailsLoading, setCallDetailsLoading] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [callDetailLoading, setCallDetailLoading] = useState(false);
  const [graphResource, setGraphResource] = useState('voice_minutes');
  const [consumptionLoading, setConsumptionLoading] = useState(false);
  const [consumptionFetched, setConsumptionFetched] = useState(false);
  const [callDetailsFetched, setCallDetailsFetched] = useState(false);

  const [purchaseFilterText, setPurchaseFilterText] = useState('');
  const [purchasePage, setPurchasePage] = useState(1);
  const PAGE_SIZE = 10;

  const [quotaFilterText, setQuotaFilterText] = useState('');
  const [quotaPage, setQuotaPage] = useState(1);

  const [consumptionFilterText, setConsumptionFilterText] = useState('');
  const [consumptionPage, setConsumptionPage] = useState(1);

  const [callDetailsFilterText, setCallDetailsFilterText] = useState('');

  const sortedPurchases = useMemo(() => {
    if (!purchaseSortingColumn) return purchases;
    const sorted = [...purchases].sort((a, b) => {
      const getVal = (item) => {
        const f = purchaseSortingColumn.sortingField;
        if (f === 'pack') return item.pack?.name || '';
        if (f === 'quantity') return item.quantity || 0;
        if (f === 'slots') return item.total_slots || 0;
        if (f === 'status') return item.status || '';
        return '';
      };
      const aVal = getVal(a);
      const bVal = getVal(b);
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
      return 0;
    });
    return purchaseSortingDescending ? [...sorted].reverse() : sorted;
  }, [purchases, purchaseSortingColumn, purchaseSortingDescending]);

  const filteredPurchases = useMemo(() => {
    if (!purchaseFilterText) return sortedPurchases;
    const q = purchaseFilterText.toLowerCase();
    return sortedPurchases.filter((p) =>
      (p.pack?.name || '').toLowerCase().includes(q) ||
      (p.status || '').toLowerCase().includes(q) ||
      (p.assigned_agent?.name || '').toLowerCase().includes(q)
    );
  }, [sortedPurchases, purchaseFilterText]);

  const paginatedPurchases = useMemo(() => {
    const start = (purchasePage - 1) * PAGE_SIZE;
    return filteredPurchases.slice(start, start + PAGE_SIZE);
  }, [filteredPurchases, purchasePage]);

  const filteredQuotaPacks = useMemo(() => {
    const packs = quotaStatus?.packs || [];
    if (!quotaFilterText) return packs;
    const q = quotaFilterText.toLowerCase();
    return packs.filter((p) =>
      (p.pack_name || '').toLowerCase().includes(q) ||
      (p.message || '').toLowerCase().includes(q)
    );
  }, [quotaStatus, quotaFilterText]);

  const paginatedQuotaPacks = useMemo(() => {
    const start = (quotaPage - 1) * PAGE_SIZE;
    return filteredQuotaPacks.slice(start, start + PAGE_SIZE);
  }, [filteredQuotaPacks, quotaPage]);

  const filteredConsumption = useMemo(() => {
    if (!consumptionFilterText) return consumptionHistory;
    const q = consumptionFilterText.toLowerCase();
    return consumptionHistory.filter((c) =>
      (c.pack_name || '').toLowerCase().includes(q) ||
      (c.agent_name || '').toLowerCase().includes(q)
    );
  }, [consumptionHistory, consumptionFilterText]);

  const paginatedConsumption = useMemo(() => {
    const start = (consumptionPage - 1) * PAGE_SIZE;
    return filteredConsumption.slice(start, start + PAGE_SIZE);
  }, [filteredConsumption, consumptionPage]);

  const filteredCallDetails = useMemo(() => {
    if (!callDetailsFilterText) return callDetails;
    const q = callDetailsFilterText.toLowerCase();
    return callDetails.filter((c) =>
      (c.agent_name || '').toLowerCase().includes(q) ||
      (c.caller_number || '').toLowerCase().includes(q) ||
      (c.transcript_preview || '').toLowerCase().includes(q)
    );
  }, [callDetails, callDetailsFilterText]);

  const accessToken = user?.access_token || user?.token;

  const axiosConfig = useMemo(() => ({
    headers: { Authorization: `Bearer ${accessToken}` },
  }), [accessToken]);

  const fetchBillingData = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const [balanceRes, packsRes, purchasesRes, quotaRes] = await Promise.all([
        axios.get('/api/billing/balance/', axiosConfig),
        axios.get('/api/billing/packs/', axiosConfig),
        axios.get('/api/billing/purchases/', axiosConfig),
        axios.get('/api/billing/quota/', axiosConfig),
      ]);
      setBalance(balanceRes.data?.data || null);
      setAvailablePacks(packsRes.data?.data || []);
      setPurchases(purchasesRes.data?.data || []);
      const quotaData = quotaRes.data?.data || { packs: [], resources: {} };
      setQuotaStatus(quotaData);
    } catch (err) {
      console.error('Error loading billing data:', err);
      notifyError('Failed to load billing data: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, [accessToken, axiosConfig, notifyError]);

  const fetchConsumptionData = useCallback(async () => {
    if (!accessToken) return;
    setConsumptionLoading(true);
    try {
      const [historyRes, graphsRes] = await Promise.all([
        axios.get('/api/billing/consumption-history/', axiosConfig),
        axios.get('/api/billing/consumption-graphs/?months=6', axiosConfig),
      ]);
      setConsumptionHistory(historyRes.data?.data || []);
      setConsumptionGraphs(graphsRes.data?.data || []);
    } catch (err) {
      console.error('Error loading consumption data:', err);
      notifyError('Failed to load consumption data: ' + (err.response?.data?.message || err.message));
    } finally {
      setConsumptionLoading(false);
      setConsumptionFetched(true);
    }
  }, [accessToken, axiosConfig, notifyError]);

  const fetchCallDetails = useCallback(async (page = 1) => {
    if (!accessToken) return;
    setCallDetailsLoading(true);
    try {
      const limit = 20;
      const offset = (page - 1) * limit;
      const res = await axios.get(`/api/billing/call-details/?limit=${limit}&offset=${offset}`, axiosConfig);
      setCallDetails(res.data?.data || []);
      setCallDetailsTotal(res.data?.total || 0);
      setCallDetailsPage(page);
    } catch (err) {
      console.error('Error loading call details:', err);
      notifyError('Failed to load call details: ' + (err.response?.data?.message || err.message));
    } finally {
      setCallDetailsLoading(false);
      setCallDetailsFetched(true);
    }
  }, [accessToken, axiosConfig, notifyError]);

  useEffect(() => {
    if (activeTab === 'consumption' && !consumptionFetched && !consumptionLoading) {
      fetchConsumptionData();
    }
    if (activeTab === 'calls' && !callDetailsFetched && !callDetailsLoading) {
      fetchCallDetails(1);
    }
  }, [activeTab, consumptionFetched, callDetailsFetched, consumptionLoading, callDetailsLoading, fetchConsumptionData, fetchCallDetails]);

  useEffect(() => {
    if (user) {
      fetchBillingData();
    }
  }, [user, fetchBillingData]);

  useEffect(() => {
    const navState = location.state;
    if (navState?.selectedPackId) {
      setActiveTab('store');
      setHighlightedPackId(navState.selectedPackId);
      window.history.replaceState({}, document.title);
      const timer = setTimeout(() => setHighlightedPackId(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  const openAssignModal = async (purchase) => {
    setSelectedPackForAssign(purchase);
    setSelectedAgentForAssign(null);
    setAssignModalOpen(true);
    try {
      const res = await axios.get('/api/agents/list/', axiosConfig);
      const agents = res.data?.data || res.data || [];
      setAssignableAgents(agents);
    } catch (err) {
      console.error('Error loading agents:', err);
      notifyError('Failed to load agents: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleAssign = async () => {
    if (!selectedAgentForAssign || !selectedPackForAssign) return;
    setAssignLoading(true);
    try {
      const agentId = selectedAgentForAssign.value;
      const res = await axios.post(
        `/api/agents/${agentId}/assign-pack/`,
        { purchase_id: selectedPackForAssign.id },
        axiosConfig
      );
      notifySuccess(res.data?.message || 'Pack assigned successfully');
      setAssignModalOpen(false);
      setSelectedPackForAssign(null);
      setSelectedAgentForAssign(null);
      await fetchBillingData();
    } catch (err) {
      console.error('Assign error:', err);
      notifyError('Assign failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setAssignLoading(false);
    }
  };

  const formatCurrency = (value, currency = 'DZD') => {
    const num = parseFloat(value);
    if (Number.isNaN(num)) return value;
    return `${num.toLocaleString()} ${currency}`;
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString();
  };

  const formatDateShort = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString();
  };

  const handleViewCall = async (call) => {
    setSelectedCall(call);
    if (!call.has_full_transcript) return;
    setCallDetailLoading(true);
    try {
      const res = await axios.get(`/api/billing/call-details/${call.id}/`, axiosConfig);
      if (res.data?.data) {
        setSelectedCall({
          ...call,
          transcript: res.data.data.transcript,
          transcript_turns: res.data.data.transcript_turns || [],
          recording_url: res.data.data.recording_url,
          call_ended_reason: res.data.data.call_ended_reason,
        });
      }
    } catch (err) {
      console.error('Error fetching call detail:', err);
    } finally {
      setCallDetailLoading(false);
    }
  };

  const graphResourceLabels = {
    voice_minutes: 'Voice Minutes',
    text_conversations: 'Text Conversations',
    storage_mb: 'Storage (MB)',
    workflows: 'Workflows',
    knowledge_bases: 'Knowledge Bases',
    twilio_numbers: 'Telephony Numbers',
  };

  const graphResourceColors = {
    voice_minutes: '#0073e6',
    text_conversations: '#037f0a',
    storage_mb: '#d87800',
    workflows: '#9b59b6',
    knowledge_bases: '#e10600',
    twilio_numbers: '#0a6b8a',
  };

  const renderBarChart = (data, resourceKey) => {
    if (!data || data.length === 0) return null;
    const maxVal = Math.max(...data.map(d => d[resourceKey] || 0), 1);
    return (
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', height: '200px', padding: '8px 0' }}>
        {data.map((item, idx) => {
          const val = item[resourceKey] || 0;
          const heightPct = (val / maxVal) * 100;
          return (
            <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: '60px' }}>
              <Box variant="small" color="text-body-secondary" style={{ marginBottom: '4px' }}>{val}</Box>
              <div style={{
                width: '80%',
                height: `${heightPct}%`,
                minHeight: val > 0 ? '4px' : '0',
                backgroundColor: graphResourceColors[resourceKey] || '#0073e6',
                borderRadius: '4px 4px 0 0',
                transition: 'height 0.3s ease',
              }} />
              <Box variant="small" color="text-body-secondary" style={{ marginTop: '4px', textAlign: 'center' }}>{item.month}</Box>
            </div>
          );
        })}
      </div>
    );
  };

  const statusTypeMap = {
    inventory: 'info',
    active: 'success',
    expired: 'error',
    suspended: 'warning',
  };

  const statusLabelMap = {
    inventory: 'In stock',
    active: 'Active',
    expired: 'Expired',
    suspended: 'Suspended',
  };

  const getPurchaseStatusIndicator = (purchase) => (
    <StatusIndicator type={statusTypeMap[purchase.status] || 'info'}>
      {statusLabelMap[purchase.status] || purchase.status || 'Unknown'}
    </StatusIndicator>
  );

  if (loading) {
    return (
      <Box textAlign="center" padding={{ top: 'xxxl' }}>
        <Spinner size="large" />
        <Box variant="h3" padding={{ top: 'm' }}>
          Loading billing data...
        </Box>
      </Box>
    );
  }

  return (
    <AppLayoutWrapper
      headerBackgroundStyle="linear-gradient(to bottom, rgba(9, 29, 43, 1) 2%, rgba(0, 108, 224, 1) 8%, rgba(255, 255, 255, 1) 82%)"
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            { text: 'Home', href: '/dashboard' },
            { text: 'Billing', href: '/billing' },
          ]}
          onFollow={(event) => {
            event.preventDefault();
            navigate(event.detail.href);
          }}
        />
      }
      header={{
        title: 'Billing & Quotas',
        description: 'Purchase packs and monitor agent quotas.',
        actions: (
          <Button
            iconName="refresh"
            onClick={() => {
              fetchBillingData();
              fetchConsumptionData();
              fetchCallDetails(callDetailsPage);
            }}
            loading={loading || consumptionLoading}
          >
            Refresh
          </Button>
        ),
      }}
    >
          <SpaceBetween size="l">
            <ColumnLayout columns={{ default: 1, s: 2, m: 3 }} variant="text-grid">
              <Container>
                <Box variant="awsui-key-label">Active Purchases</Box>
                <Box variant="h1">{balance?.active_purchases_count || 0}</Box>
              </Container>
              <Container>
                <Box variant="awsui-key-label">Total Slots</Box>
                <Box variant="h1">{balance?.total_active_slots || 0}</Box>
              </Container>
              <Container>
                <Box variant="awsui-key-label">Used Slots</Box>
                <Box variant="h1">{balance?.used_active_slots || 0}</Box>
              </Container>
            </ColumnLayout>

            <Tabs
              activeTabId={activeTab}
              onChange={({ detail }) => setActiveTab(detail.activeTabId)}
              tabs={[
                {
                  id: 'store',
                  label: 'Pack Store',
                  content: (
                    <SpaceBetween size="l">
                      <Alert type="info">
                        Purchase additional packs on Adexcloud to increase agent quotas and resource limits.
                      </Alert>
                      <Cards
                        cardDefinition={{
                          header: (item) => (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '1.25rem', fontWeight: 700 }}>{item.name}</span>
                              {item.is_default && <Badge color="blue">Default</Badge>}
                              <Badge color={item.billing_cycle === 'yearly' ? 'green' : 'grey'}>
                                {item.billing_cycle === 'yearly' ? 'Annual' : 'Monthly'}
                              </Badge>
                              {highlightedPackId === item.id && (
                                <Badge color="green">Selected</Badge>
                              )}
                            </div>
                          ),
                          sections: [
                            {
                              id: 'details',
                              content: (item) => (
                                <SpaceBetween size="s">
                                  <Box variant="p">{item.description}</Box>
                                  <ColumnLayout columns={2} variant="text-grid">
                                    <div>
                                      <Box variant="awsui-key-label">Price</Box>
                                      <Box variant="h2">{formatCurrency(item.cycle_price, item.currency)}</Box>
                                      <Box variant="small">{item.billing_cycle === 'yearly' ? 'per year' : 'per month'}</Box>
                                    </div>
                                    <div>
                                      <Box variant="awsui-key-label">Your agent slots</Box>
                                      <Box variant="h2">
                                        {item.company_quota?.used || 0} / {item.company_quota?.total || 0}
                                      </Box>
                                      <Box variant="small">
                                        {item.company_quota?.remaining || 0} slots available (1 per pack)
                                      </Box>
                                    </div>
                                  </ColumnLayout>
                                  <Box variant="awsui-key-label">Limits per pack</Box>
                                  <Box variant="p">
                                    1 agent, {item.cycle_minutes} voice minutes, {item.cycle_conversations} text conversations/{item.billing_cycle === 'yearly' ? 'year' : 'month'},{' '}
                                    {item.document_storage_mb} MB storage, {item.max_workflows} workflows, {item.max_knowledge_bases} KBs
                                  </Box>
                                </SpaceBetween>
                              ),
                            },
                            {
                              id: 'purchase',
                              content: (item) => {
                                const purchaseUrl = getAdexcloudPurchaseUrl(item);
                                return (
                                  <SpaceBetween size="s" direction="horizontal">
                                    <Button
                                      variant="primary"
                                      iconName="external"
                                      href={purchaseUrl}
                                      target="_blank"
                                      disabled={!purchaseUrl}
                                    >
                                      Buy on Adexcloud
                                    </Button>
                                  </SpaceBetween>
                                );
                              },
                            },
                          ],
                        }}
                        cardsPerRow={[{ cards: 1 }, { minWidth: 500, cards: 2 }]}
                        items={availablePacks}
                        loadingText="Loading packs"
                        trackBy="id"
                        empty={
                          <Box textAlign="center" color="text-body-secondary">
                            No packs available.
                          </Box>
                        }
                      />
                    </SpaceBetween>
                  ),
                },
                {
                  id: 'purchases',
                  label: 'My Packs',
                  content: (
                    <SpaceBetween size="l">
                      <Alert type="info">
                        Packs bought from the store are placed in stock. They become active when assigned to an agent.
                      </Alert>

                      <Table
                        columnDefinitions={[
                          { id: 'pack', header: 'Pack', cell: (item) => (
                            <SpaceBetween size="xs">
                              <span>{item.pack.name}</span>
                              <Badge color={item.pack.billing_cycle === 'yearly' ? 'green' : 'grey'}>
                                {item.pack.billing_cycle === 'yearly' ? 'Annual' : 'Monthly'}
                              </Badge>
                            </SpaceBetween>
                          ), sortingField: 'pack' },
                          { id: 'quantity', header: 'Quantity', cell: (item) => item.quantity, sortingField: 'quantity' },
                          { id: 'slots', header: 'Slots', cell: (item) => `${item.used_slots}/${item.total_slots}`, sortingField: 'slots' },
                          {
                            id: 'status',
                            header: 'Status',
                            cell: (item) => getPurchaseStatusIndicator(item),
                            sortingField: 'status',
                          },
                          {
                            id: 'agent',
                            header: 'Agent',
                            cell: (item) => (
                              item.assigned_agent ? (
                                <span>{item.assigned_agent.name}</span>
                              ) : (
                                <Box variant="p" color="text-body-secondary">Unassigned</Box>
                              )
                            ),
                          },
                          {
                            id: 'cycle',
                            header: 'Cycle',
                            cell: (item) => (
                              <span>
                                {formatDate(item.cycle_start)} → {formatDate(item.cycle_end)}
                              </span>
                            ),
                          },
                          {
                            id: 'actions',
                            header: 'Actions',
                            cell: (item) =>
                              item.status === 'inventory' && item.remaining_slots > 0 ? (
                                <Button onClick={() => openAssignModal(item)}>Assign to agent</Button>
                              ) : (
                                '-'
                              ),
                          },
                        ]}
                        items={paginatedPurchases}
                        loadingText="Loading packs"
                        trackBy="id"
                        stickyHeader
                        sortable
                        sortingColumn={purchaseSortingColumn}
                        sortingDescending={purchaseSortingDescending}
                        onSortingChange={({ detail }) => {
                          setPurchaseSortingColumn(detail.sortingColumn);
                          setPurchaseSortingDescending(detail.isDescending);
                        }}
                        filter={
                          <TextFilter
                            filteringText={purchaseFilterText}
                            filteringPlaceholder="Search packs, status, agent..."
                            onChange={({ detail }) => { setPurchaseFilterText(detail.filteringText); setPurchasePage(1); }}
                          />
                        }
                        pagination={
                          <Pagination
                            currentPageIndex={purchasePage}
                            pagesCount={Math.max(1, Math.ceil(filteredPurchases.length / PAGE_SIZE))}
                            onChange={({ detail }) => setPurchasePage(detail.currentPageIndex)}
                          />
                        }
                        empty={
                          <Box textAlign="center" color="text-body-secondary">
                            No packs yet. Buy a pack from the Pack Store.
                          </Box>
                        }
                      />

                      <Header variant="h2" counter={`(${purchases.filter((p) => p.status === 'inventory').length})`}>In stock</Header>
                      {(purchases.filter((p) => p.status === 'inventory') || []).length === 0 ? (
                        <Box variant="p" color="text-body-secondary">No packs in stock.</Box>
                      ) : (
                        purchases
                          .filter((p) => p.status === 'inventory')
                          .map((purchase) => (
                            <Container
                              key={purchase.id}
                              header={
                                <Header
                                  variant="h2"
                                  actions={
                                    <Button onClick={() => openAssignModal(purchase)}>
                                      Assign to agent
                                    </Button>
                                  }
                                >
                                  {purchase.pack.name} x{purchase.quantity}
                                </Header>
                              }
                            >
                              <SpaceBetween size="s">
                                <Box variant="awsui-key-label">Status</Box>
                                {getPurchaseStatusIndicator(purchase)}
                                <Box variant="awsui-key-label">Available slots</Box>
                                <Box variant="p">{purchase.remaining_slots} / {purchase.total_slots}</Box>
                              </SpaceBetween>
                            </Container>
                          ))
                      )}

                      <Header variant="h2" counter={`(${purchases.filter((p) => p.status === 'active').length})`}>Active</Header>
                      {(purchases.filter((p) => p.status === 'active') || []).length === 0 ? (
                        <Box variant="p" color="text-body-secondary">No active packs.</Box>
                      ) : (
                        purchases
                          .filter((p) => p.status === 'active')
                          .map((purchase) => (
                            <Container
                              key={purchase.id}
                              header={
                                <Header variant="h2" actions={getPurchaseStatusIndicator(purchase)}>
                                  {purchase.pack.name} x{purchase.quantity}
                                </Header>
                              }
                            >
                              <SpaceBetween size="s">
                                <ColumnLayout columns={{ default: 1, s: 2 }} variant="text-grid">
                                  <div>
                                    <Box variant="awsui-key-label">Agent</Box>
                                    <Box variant="p">
                                      {purchase.assigned_agent?.name || 'Unknown'}
                                    </Box>
                                  </div>
                                  <div>
                                    <Box variant="awsui-key-label">Cycle</Box>
                                    <Box variant="p">
                                      {formatDate(purchase.cycle_start)} → {formatDate(purchase.cycle_end)}
                                    </Box>
                                    <Box variant="small">{purchase.pack?.billing_cycle === 'yearly' ? 'Annual cycle' : 'Monthly cycle'}</Box>
                                  </div>
                                </ColumnLayout>
                                <ColumnLayout columns={{ default: 2, s: 4 }} variant="text-grid">
                                  <div>
                                    <Box variant="awsui-key-label">Voice Minutes</Box>
                                    <Box variant="p">{purchase.resources?.cycle_minutes || 0} / {purchase.pack?.billing_cycle === 'yearly' ? 'year' : 'month'}</Box>
                                  </div>
                                  <div>
                                    <Box variant="awsui-key-label">Text Conversations</Box>
                                    <Box variant="p">{purchase.resources?.cycle_conversations || 0} / {purchase.pack?.billing_cycle === 'yearly' ? 'year' : 'month'}</Box>
                                  </div>
                                  <div>
                                    <Box variant="awsui-key-label">Storage</Box>
                                    <Box variant="p">{purchase.resources?.document_storage_mb || 0} MB</Box>
                                  </div>
                                  <div>
                                    <Box variant="awsui-key-label">Workflows</Box>
                                    <Box variant="p">{purchase.resources?.max_workflows || 0}</Box>
                                  </div>
                                  <div>
                                    <Box variant="awsui-key-label">Knowledge Bases</Box>
                                    <Box variant="p">{purchase.resources?.max_knowledge_bases || 0}</Box>
                                  </div>
                                </ColumnLayout>
                              </SpaceBetween>
                            </Container>
                          ))
                      )}

                      <Header variant="h2" counter={`(${purchases.filter((p) => p.status === 'expired' || p.status === 'suspended').length})`}>Expired / Suspended</Header>
                      {(purchases.filter((p) => p.status === 'expired' || p.status === 'suspended') || []).length === 0 ? (
                        <Box variant="p" color="text-body-secondary">No expired or suspended packs.</Box>
                      ) : (
                        purchases
                          .filter((p) => p.status === 'expired' || p.status === 'suspended')
                          .map((purchase) => (
                            <Container
                              key={purchase.id}
                              header={
                                <Header variant="h2" actions={getPurchaseStatusIndicator(purchase)}>
                                  {purchase.pack.name} x{purchase.quantity}
                                </Header>
                              }
                            >
                              <SpaceBetween size="s">
                                <Box variant="awsui-key-label">Cycle ended</Box>
                                <Box variant="p">{formatDate(purchase.cycle_end)}</Box>
                              </SpaceBetween>
                            </Container>
                          ))
                      )}
                    </SpaceBetween>
                  ),
                },
                {
                  id: 'quotas',
                  label: 'Quota Status',
                  content: (
                    <SpaceBetween size="l">
                      <Container header={<Header variant="h2" description="Resource consumption across all active packs">Resource usage</Header>}>
                        <ColumnLayout columns={4} variant="text-grid">
                          <div>
                            <Box variant="small">Voice minutes / cycle</Box>
                            <Box variant="h1">
                              {quotaStatus?.resources?.voice_minutes?.used || 0} /{' '}
                              {quotaStatus?.resources?.voice_minutes?.total || 0}
                            </Box>
                          </div>
                          <div>
                            <Box variant="small">Text conversations / cycle</Box>
                            <Box variant="h1">
                              {quotaStatus?.resources?.text_conversations?.used || 0} /{' '}
                              {quotaStatus?.resources?.text_conversations?.total || 0}
                            </Box>
                          </div>
                          <div>
                            <Box variant="small">Storage (MB)</Box>
                            <Box variant="h1">
                              {quotaStatus?.resources?.document_storage_mb?.used || 0} /{' '}
                              {quotaStatus?.resources?.document_storage_mb?.total || 0}
                            </Box>
                          </div>
                          <div>
                            <Box variant="small">Knowledge Bases</Box>
                            <Box variant="h1">
                              {quotaStatus?.resources?.knowledge_bases?.used || 0} /{' '}
                              {quotaStatus?.resources?.knowledge_bases?.total || 0}
                            </Box>
                          </div>
                          <div>
                            <Box variant="small">Workflows</Box>
                            <Box variant="h1">
                              {quotaStatus?.resources?.workflows?.used || 0} /{' '}
                              {quotaStatus?.resources?.workflows?.total || 0}
                            </Box>
                          </div>
                        </ColumnLayout>
                      </Container>

                      <Table
                        columnDefinitions={[
                          { id: 'pack', header: 'Pack', cell: (item) => item.pack_name, sortingField: 'pack' },
                          { id: 'cycle_type', header: 'Cycle', cell: (item) => (
                            <Badge color={item.billing_cycle === 'yearly' ? 'green' : 'grey'}>
                              {item.billing_cycle === 'yearly' ? 'Annual' : 'Monthly'}
                            </Badge>
                          ) },
                          {
                            id: 'can_create',
                            header: 'Can create agent',
                            cell: (item) => (
                              <StatusIndicator type={item.can_create ? 'success' : 'error'}>
                                {item.can_create ? 'Yes' : 'No'}
                              </StatusIndicator>
                            ),
                            sortingField: 'can_create',
                          },
                          { id: 'total', header: 'Total slots', cell: (item) => item.total_slots, sortingField: 'total' },
                          { id: 'used', header: 'Used slots', cell: (item) => item.used_slots, sortingField: 'used' },
                          { id: 'remaining', header: 'Remaining', cell: (item) => item.remaining_slots, sortingField: 'remaining' },
                          {
                            id: 'expired',
                            header: 'Expired packs',
                            cell: (item) => (
                              <StatusIndicator type={(item.expired_slots || 0) > 0 ? 'warning' : 'success'}>
                                {item.expired_slots || 0}
                              </StatusIndicator>
                            ),
                            sortingField: 'expired',
                          },
                          { id: 'message', header: 'Message', cell: (item) => item.message || '-' },
                        ]}
                        items={paginatedQuotaPacks}
                        loadingText="Loading quotas"
                        trackBy="pack_id"
                        filter={
                          <TextFilter
                            filteringText={quotaFilterText}
                            filteringPlaceholder="Search packs..."
                            onChange={({ detail }) => { setQuotaFilterText(detail.filteringText); setQuotaPage(1); }}
                          />
                        }
                        pagination={
                          <Pagination
                            currentPageIndex={quotaPage}
                            pagesCount={Math.max(1, Math.ceil(filteredQuotaPacks.length / PAGE_SIZE))}
                            onChange={({ detail }) => setQuotaPage(detail.currentPageIndex)}
                          />
                        }
                        empty={
                          <Box textAlign="center" color="text-body-secondary">
                            No quota data available.
                          </Box>
                        }
                      />
                    </SpaceBetween>
                  ),
                },
                {
                  id: 'consumption',
                  label: 'Consumption',
                  content: (
                    <SpaceBetween size="l">
                      {consumptionLoading && (
                        <Box textAlign="center" padding={{ top: 'l' }}>
                          <Spinner size="large" />
                          <Box variant="p" padding={{ top: 'm' }}>Loading consumption data...</Box>
                        </Box>
                      )}
                      {!consumptionLoading && (
                        <>
                          <Container header={<Header variant="h2" description="Consumption per billing cycle, broken down by resource type">Consumption History by Cycle</Header>}>
                            <Table
                              columnDefinitions={[
                                { id: 'pack', header: 'Pack', cell: (item) => (
                                  <SpaceBetween size="xs">
                                    <span>{item.pack_name}</span>
                                    <Badge color={item.billing_cycle === 'yearly' ? 'green' : 'grey'}>
                                      {item.billing_cycle === 'yearly' ? 'Annual' : 'Monthly'}
                                    </Badge>
                                  </SpaceBetween>
                                ) },
                                { id: 'agent', header: 'Agent', cell: (item) => item.agent_name || '-' },
                                { id: 'cycle', header: 'Cycle', cell: (item) => `${formatDateShort(item.cycle_start)} → ${formatDateShort(item.cycle_end)}` },
                                { id: 'voice', header: 'Voice Min', cell: (item) => item.resources.voice_minutes },
                                { id: 'text', header: 'Text Conv.', cell: (item) => item.resources.text_conversations },
                                { id: 'storage', header: 'Storage (MB)', cell: (item) => item.resources.storage_mb },
                                { id: 'wf', header: 'Workflows', cell: (item) => item.resources.workflows },
                                { id: 'kb', header: 'KBs', cell: (item) => item.resources.knowledge_bases },
                                { id: 'tel', header: 'Telephony', cell: (item) => item.resources.twilio_numbers },
                              ]}
                              items={paginatedConsumption}
                              trackBy="purchase_id"
                              stickyHeader
                              filter={
                                <TextFilter
                                  filteringText={consumptionFilterText}
                                  filteringPlaceholder="Search pack, agent..."
                                  onChange={({ detail }) => { setConsumptionFilterText(detail.filteringText); setConsumptionPage(1); }}
                                />
                              }
                              pagination={
                                <Pagination
                                  currentPageIndex={consumptionPage}
                                  pagesCount={Math.max(1, Math.ceil(filteredConsumption.length / PAGE_SIZE))}
                                  onChange={({ detail }) => setConsumptionPage(detail.currentPageIndex)}
                                />
                              }
                              empty={
                                <Box textAlign="center" color="text-body-secondary">
                                  No consumption history available yet.
                                </Box>
                              }
                            />
                          </Container>

                          <Container header={<Header variant="h2" description="Monthly consumption over the last 6 months">Monthly Consumption Graphs</Header>}>
                            <SpaceBetween size="l">
                              <FormField label="Resource type">
                                <Select
                                  selectedOption={{ label: graphResourceLabels[graphResource], value: graphResource }}
                                  onChange={({ detail }) => setGraphResource(detail.selectedOption.value)}
                                  options={Object.entries(graphResourceLabels).map(([key, label]) => ({ label, value: key }))}
                                />
                              </FormField>
                              {consumptionGraphs.length > 0 ? (
                                renderBarChart(consumptionGraphs, graphResource)
                              ) : (
                                <Box textAlign="center" color="text-body-secondary">No graph data available.</Box>
                              )}
                            </SpaceBetween>
                          </Container>
                        </>
                      )}
                    </SpaceBetween>
                  ),
                },
                {
                  id: 'calls',
                  label: 'Call Details',
                  content: (
                    <SpaceBetween size="l">
                      <Alert type="info">
                        Call log showing transcription, duration, date, and caller number only.
                      </Alert>
                      {callDetailsLoading && (
                        <Box textAlign="center" padding={{ top: 'l' }}>
                          <Spinner size="large" />
                          <Box variant="p" padding={{ top: 'm' }}>Loading call details...</Box>
                        </Box>
                      )}
                      {!callDetailsLoading && (
                        <Table
                          columnDefinitions={[
                            { id: 'date', header: 'Date', cell: (item) => formatDate(item.created_at) },
                            { id: 'agent', header: 'Agent', cell: (item) => item.agent_name },
                            { id: 'duration', header: 'Duration', cell: (item) => item.duration_formatted },
                            { id: 'caller', header: 'Caller Number', cell: (item) => item.caller_number || '-' },
                            {
                              id: 'transcript',
                              header: 'Transcript',
                              cell: (item) => (
                                <Box variant="small" color="text-body-secondary">
                                  {item.transcript_preview ? (item.transcript_preview.length > 80 ? item.transcript_preview.substring(0, 80) + '...' : item.transcript_preview) : '-'}
                                </Box>
                              ),
                            },
                            {
                              id: 'actions',
                              header: 'Actions',
                              cell: (item) => (
                                <Button variant="link" onClick={() => handleViewCall(item)}>View</Button>
                              ),
                            },
                          ]}
                          items={filteredCallDetails}
                          trackBy="id"
                          stickyHeader
                          filter={
                            <TextFilter
                              filteringText={callDetailsFilterText}
                              filteringPlaceholder="Search agent, caller, transcript..."
                              onChange={({ detail }) => setCallDetailsFilterText(detail.filteringText)}
                            />
                          }
                          pagination={
                            <Pagination
                              currentPageIndex={callDetailsPage}
                              pagesCount={Math.max(1, Math.ceil(callDetailsTotal / 20))}
                              onChange={({ detail }) => fetchCallDetails(detail.currentPageIndex)}
                            />
                          }
                          empty={
                            <Box textAlign="center" color="text-body-secondary">
                              No calls recorded yet.
                            </Box>
                          }
                        />
                      )}
                    </SpaceBetween>
                  ),
                },
              ]}
            />
          </SpaceBetween>

      <Modal
        visible={assignModalOpen}
        onDismiss={() => setAssignModalOpen(false)}
        header="Assign pack to agent"
        closeAriaLabel="Close"
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => setAssignModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={assignLoading}
              disabled={!selectedAgentForAssign}
              onClick={handleAssign}
            >
              Assign
            </Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween size="m">
          <Box variant="p">
            Select the agent that will consume pack{' '}
            <Box variant="strong">{selectedPackForAssign?.pack?.name}</Box>.
          </Box>
          <FormField label="Agent">
            <Select
              selectedOption={selectedAgentForAssign}
              onChange={({ detail }) => setSelectedAgentForAssign(detail.selectedOption)}
              options={assignableAgents.map((agent) => ({
                label: agent.name || 'Unnamed Agent',
                value: agent.id || agent.adexcloud_agent_id,
              }))}
              placeholder="Choose an agent"
            />
          </FormField>
        </SpaceBetween>
      </Modal>

      <Modal
        visible={!!selectedCall}
        onDismiss={() => setSelectedCall(null)}
        header={`Call Details - ${selectedCall?.agent_name || ''}`}
        closeAriaLabel="Close"
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => setSelectedCall(null)}>Close</Button>
          </SpaceBetween>
        }
      >
        {selectedCall && (
          <SpaceBetween size="m">
            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Date</Box>
                <Box variant="p">{formatDate(selectedCall.created_at)}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Duration</Box>
                <Box variant="p">{selectedCall.duration_formatted}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Caller Number</Box>
                <Box variant="p">{selectedCall.caller_number || '-'}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Agent</Box>
                <Box variant="p">{selectedCall.agent_name}</Box>
              </div>
              {selectedCall.call_ended_reason && (
                <div>
                  <Box variant="awsui-key-label">End Reason</Box>
                  <Box variant="p">{selectedCall.call_ended_reason}</Box>
                </div>
              )}
              {selectedCall.recording_url && (
                <div>
                  <Box variant="awsui-key-label">Recording</Box>
                  <audio controls src={selectedCall.recording_url} style={{ width: '100%' }} />
                </div>
              )}
            </ColumnLayout>
            <Box variant="awsui-key-label">Transcript</Box>
            {callDetailLoading ? (
              <Box textAlign="center" padding={{ top: 'l' }}>
                <Spinner size="normal" />
                <Box variant="p" padding={{ top: 's' }}>Loading transcript...</Box>
              </Box>
            ) : selectedCall.transcript_turns && selectedCall.transcript_turns.length > 0 ? (
              <div style={{
                maxHeight: '400px',
                overflowY: 'auto',
                border: '1px solid #e9ebed',
                borderRadius: '8px',
                padding: '16px',
                background: '#f8f9fa',
              }}>
                <SpaceBetween size="m">
                  {selectedCall.transcript_turns.map((turn, idx) => {
                    const isAgent = turn.role === 'agent';
                    return (
                      <div key={idx} style={{
                        display: 'flex',
                        flexDirection: isAgent ? 'row' : 'row-reverse',
                        gap: '10px',
                        alignItems: 'flex-start',
                      }}>
                        <div style={{
                          flexShrink: 0,
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: isAgent ? '#0073e6' : '#037f0a',
                          color: '#fff',
                        }}>
                          <Icon name={isAgent ? 'gen-ai' : 'user-profile'} size="medium" />
                        </div>
                        <div style={{
                          maxWidth: '75%',
                          padding: '10px 14px',
                          borderRadius: '12px',
                          background: isAgent ? '#fff' : '#e4f4e4',
                          border: '1px solid #e9ebed',
                        }}>
                          <Box variant="small" fontWeight="bold" color="text-body-secondary" style={{ marginBottom: '4px' }}>
                            {isAgent ? (selectedCall.agent_name || 'Agent') : 'User'}
                          </Box>
                          <div className="transcript-markdown">
                            <ReactMarkdown>{turn.text}</ReactMarkdown>
                          </div>
                          {turn.timestamp && (
                            <Box variant="small" color="text-body-secondary" style={{ marginTop: '4px' }}>
                              {new Date(turn.timestamp).toLocaleTimeString()}
                            </Box>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </SpaceBetween>
              </div>
            ) : (
              <div className="transcript-markdown" style={{
                maxHeight: '400px',
                overflowY: 'auto',
                border: '1px solid #e9ebed',
                borderRadius: '8px',
                padding: '16px',
                background: '#f8f9fa',
              }}>
                <ReactMarkdown>
                  {selectedCall.transcript || selectedCall.transcript_preview || 'No transcript available.'}
                </ReactMarkdown>
              </div>
            )}
          </SpaceBetween>
        )}
      </Modal>
    </AppLayoutWrapper>
  );
}
